import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { slugify } from '../../utils/slugify';
import ServicesHero from './ServicesHero';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import HealingIcon from '@mui/icons-material/Healing';
import ScienceIcon from '@mui/icons-material/Science';
import EmergencyIcon from '@mui/icons-material/LocalHospital';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import DepartmentIcon from '../../utils/departmentIcons';

const Departments = () => {
  const { state } = useData();
  const { departments, doctors } = state;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Group departments by clinic category
  const allCategories = [
    { id: 'Medical Clinics', name: 'Medical Clinics', icon: <MedicalServicesIcon /> },
    { id: 'Surgical Clinics', name: 'Surgical Clinics', icon: <HealingIcon /> },
    { id: 'Diagnostic Services', name: 'Diagnostic Services', icon: <ScienceIcon /> },
    { id: 'Emergency Services', name: 'Emergency Services', icon: <EmergencyIcon /> }
  ];

  const groupedDepartments = {
    'Medical Clinics': departments.filter(dept => 
      dept.service_category === 'Medical Clinics' && 
      (dept.is_active === true || dept.is_active === 1)
    ),
    'Surgical Clinics': departments.filter(dept => 
      dept.service_category === 'Surgical Clinics' && 
      (dept.is_active === true || dept.is_active === 1)
    ),
    'Diagnostic Services': departments.filter(dept => 
      dept.service_category === 'Diagnostic Services' && 
      (dept.is_active === true || dept.is_active === 1)
    ),
    'Emergency Services': departments.filter(dept => 
      dept.service_category === 'Emergency Services' && 
      (dept.is_active === true || dept.is_active === 1)
    )
  };

  // Filter departments based on search term
  const filterDepartments = (deptList) => {
    return deptList.filter(dept =>
      dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dept.description && dept.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  // Calculate total departments count
  const totalDepartments = Object.values(groupedDepartments).reduce(
    (sum, deptList) => sum + deptList.length,
    0
  );

  return (
    <Box>
      {/* Hero Section */}
      <ServicesHero 
        title="Departments"
        subtitle="Explore our comprehensive range of medical departments and services"
        serviceType="departments"
      />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Overview */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            All Our Departments
          </Typography>
          <Typography variant="h6" paragraph sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}>
            Hearts International Hospital offers a comprehensive range of medical departments 
            covering medical, surgical, diagnostic, and emergency services. Our expert teams 
            provide world-class healthcare across all specialties.
          </Typography>
      
        </Box>

        {/* Search Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <TextField
            placeholder="Search departments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: { xs: '100%', sm: '500px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '25px',
                backgroundColor: 'white',
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Departments by Category */}
        {allCategories.map((category) => {
          const filtered = filterDepartments(groupedDepartments[category.id]);
          if (filtered.length === 0) return null;
          
          return (
            <Box key={category.id} sx={{ mb: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  fontSize: '2rem', 
                  mr: 2,
                  color: 'primary.main'
                }}>
                  {category.icon}
                </Box>
                <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
                  {category.name}
                </Typography>
                <Chip 
                  label={`${filtered.length} ${filtered.length === 1 ? 'Department' : 'Departments'}`}
                  color="primary"
                  sx={{ ml: 2 }}
                />
              </Box>
              <Divider sx={{ mb: 4 }} />
              <Grid container spacing={3}>
                {filtered.map((department) => (
                  <Grid item xs={12} sm={6} md={4} key={department.id}>
                    <Card sx={{ 
                      height: '100%',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      '&:hover': { 
                        transform: 'translateY(-8px)', 
                        boxShadow: 6,
                        '& .hover-overlay': {
                          opacity: 1,
                          visibility: 'visible'
                        },
                        '& .card-content': {
                          opacity: 0.3
                        }
                      }
                    }} onClick={() => navigate(`/department/${slugify(department.name)}`)}>
                      <CardContent className="card-content" sx={{ transition: 'opacity 0.3s ease' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <DepartmentIcon iconName={department.icon} sx={{ fontSize: 36, color: 'primary.main', mr: 1.5 }} />
                          <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            {department.name}
                          </Typography>
                        </Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          paragraph
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.6
                          }}
                        >
                          {department.description || 'Expert care in this specialized department.'}
                        </Typography>
                        
                        {/* Contact Information */}
                        {department.show_phone && department.phone && (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                              <Typography variant="body2" color="text.secondary">
                                {department.phone}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                      {/* Hover Overlay */}
                      <Box
                        className="hover-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(128, 128, 128, 0.5)',
                          opacity: 0,
                          visibility: 'hidden',
                          transition: 'all 0.3s ease',
                          zIndex: 1
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/department/${slugify(department.name)}`);
                          }}
                        >
                          View Details
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}

        {/* No results message */}
        {searchTerm && totalDepartments === 0 && (
          <Card sx={{ p: 6, textAlign: 'center' }}>
            <LocalHospitalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Departments Found
            </Typography>
            <Typography color="text.secondary">
              No departments match "{searchTerm}". Try different keywords.
            </Typography>
          </Card>
        )}

        {/* Call to Action */}
        <Box sx={{ mt: 8, p: 6, background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', borderRadius: 2, textAlign: 'center', color: 'white' }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
            Need Help Choosing a Department?
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 600, mx: 'auto', color: 'rgba(255,255,255,0.9)' }}>
            Our friendly staff is here to help you find the right department for your healthcare needs. 
            Contact us today to schedule a consultation or learn more about our services.
          </Typography>
          <Chip 
            label="Contact Us" 
            sx={{ 
              fontSize: '1.1rem', 
              py: 1,
              backgroundColor: 'white',
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)'
              }
            }}
            onClick={() => navigate('/contact')}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Departments;

